import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";

/**
 * /health/glp1 (BW18 PR-I).
 *
 * Three panels:
 *   1. Hunger trend chart, 90 days. Shows the rolling 7-day hunger average
 *      and the raw points. Surfaces the calibration story: hunger at meal
 *      time drops as titration takes effect.
 *   2. Drug history (current dose, last titration, side effects).
 *   3. Nausea trigger pattern: compares average fat / kcal of meals that
 *      were flagged nauseous vs. not, surfaces the delta after >=5 yes-flagged
 *      and >=5 no-flagged meals exist.
 */

type MealForGlp = {
  id: string;
  ts: string;
  kcal: number;
  macros: { p?: number; c?: number; f?: number } | null;
  hunger_before: number | null;
  felt_nauseous: boolean | null;
};

export function HealthGlp1() {
  const profile = useAthleteProfile();
  const onGlp1 = Boolean(profile.data?.on_glp1);

  return (
    <article aria-labelledby="glp1-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        <Link to="/health" style={{ color: "var(--fg-3)" }}>
          Health
        </Link>{" "}
        · GLP-1
      </p>
      <h1
        id="glp1-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-8)",
        }}
      >
        Eat to schedule. Watch hunger return.
      </h1>

      {!onGlp1 ? (
        <NotEnabledCard />
      ) : (
        <div style={{ display: "grid", gap: "var(--space-6)" }}>
          <DrugCard />
          <HungerTrendChart />
          <NauseaPatternCard />
        </div>
      )}
    </article>
  );
}

function NotEnabledCard() {
  return (
    <section
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
      }}
    >
      <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body)" }}>
        GLP-1 awareness is off. Toggle it in{" "}
        <Link to="/settings" style={{ color: "var(--accent)" }}>
          /settings
        </Link>{" "}
        to start logging hunger before meals and nausea afterwards.
      </p>
    </section>
  );
}

function DrugCard() {
  const profile = useAthleteProfile();
  const data = profile.data;
  if (!data) return null;
  const drug = data.glp1_drug ? capitalize(data.glp1_drug) : null;
  const dose = data.glp1_dose_mg != null ? `${data.glp1_dose_mg} mg` : null;
  const titration = data.glp1_last_titration ?? null;
  const sides = Array.isArray(data.glp1_side_effects)
    ? (data.glp1_side_effects as string[])
    : [];

  return (
    <section
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "var(--space-3)",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "var(--type-body-l)",
            fontWeight: 500,
          }}
        >
          Current protocol
        </h2>
        <Link
          to="/settings"
          style={{ color: "var(--accent)", fontSize: "var(--type-meta)" }}
        >
          Edit →
        </Link>
      </header>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "var(--space-3)",
          margin: 0,
        }}
      >
        <Stat label="Drug" value={drug ?? "—"} />
        <Stat label="Dose" value={dose ?? "—"} />
        <Stat label="Last titration" value={titration ?? "—"} />
      </dl>
      {sides.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
            paddingTop: "var(--space-2)",
            borderTop: "1px solid var(--hair)",
          }}
        >
          <span
            style={{
              fontSize: "var(--type-meta)",
              color: "var(--fg-3)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              alignSelf: "center",
              marginRight: "var(--space-2)",
            }}
          >
            Side effects
          </span>
          {sides.map((s) => (
            <span
              key={s}
              style={{
                padding: "2px 10px",
                background: "var(--chip)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-chip)",
                fontSize: "var(--type-meta)",
                color: "var(--fg-2)",
                textTransform: "capitalize",
              }}
            >
              {s.replace("_", " ")}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt
        style={{
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </dt>
      <dd
        className="mono"
        style={{
          margin: "2px 0 0",
          color: "var(--fg)",
          fontSize: "var(--type-body)",
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function HungerTrendChart() {
  const meals = useGlpMeals(90);

  const points = useMemo(() => {
    const rows = meals.data ?? [];
    const byDay = new Map<string, { sum: number; n: number }>();
    for (const r of rows) {
      if (r.hunger_before == null) continue;
      const date = r.ts.slice(0, 10);
      const acc = byDay.get(date) ?? { sum: 0, n: 0 };
      acc.sum += r.hunger_before;
      acc.n += 1;
      byDay.set(date, acc);
    }
    const list = [...byDay.entries()]
      .map(([date, { sum, n }]) => ({ date, avg: sum / n, n }))
      .sort((a, b) => a.date.localeCompare(b.date));
    return list.map((row, idx, arr) => {
      const window = arr.slice(Math.max(0, idx - 6), idx + 1);
      const rolling = window.reduce((s, p) => s + p.avg, 0) / window.length;
      return { ...row, rolling };
    });
  }, [meals.data]);

  const insight = useMemo(() => buildHungerInsight(points), [points]);
  const total = points.reduce((s, p) => s + p.n, 0);

  return (
    <section
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <h2 style={{ margin: 0, fontSize: "var(--type-body-l)", fontWeight: 500 }}>
          Hunger before meals
        </h2>
        <span
          className="mono"
          style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}
        >
          {total} log{total === 1 ? "" : "s"} · 90d
        </span>
      </header>

      {points.length === 0 ? (
        <p
          style={{
            margin: 0,
            color: "var(--fg-3)",
            fontSize: "var(--type-body-s)",
            padding: "var(--space-4) 0",
          }}
        >
          Log hunger on a few meals and the trend will surface here.
        </p>
      ) : (
        <>
          <div style={{ height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={points}>
                <XAxis
                  dataKey="date"
                  tick={{ fill: "var(--fg-3)", fontSize: 11 }}
                  stroke="var(--hair-2)"
                  tickFormatter={(d: string) => d.slice(5)}
                />
                <YAxis
                  domain={[0, 10]}
                  tick={{ fill: "var(--fg-3)", fontSize: 11 }}
                  stroke="var(--hair-2)"
                />
                <ReferenceLine y={5} stroke="var(--hair-2)" strokeDasharray="3 3" />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg-3)",
                    border: "1px solid var(--hair-2)",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                  formatter={(value: number) => value.toFixed(1)}
                  labelFormatter={(d: string) => d}
                />
                <Line
                  type="monotone"
                  dataKey="avg"
                  stroke="var(--fg-3)"
                  strokeWidth={1}
                  dot={{ r: 2 }}
                  name="Daily avg"
                />
                <Line
                  type="monotone"
                  dataKey="rolling"
                  stroke="var(--accent)"
                  strokeWidth={2}
                  dot={false}
                  name="7-day"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {insight && (
            <p
              style={{
                margin: 0,
                padding: "var(--space-3)",
                background: "var(--accent-wash)",
                borderRadius: "var(--radius-card)",
                fontSize: "var(--type-body-s)",
                color: "var(--fg)",
                lineHeight: 1.5,
              }}
            >
              {insight}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function buildHungerInsight(
  points: Array<{ date: string; rolling: number; n: number }>,
): string | null {
  if (points.length < 14) return null;
  const first = points[0];
  const last = points[points.length - 1];
  const delta = last.rolling - first.rolling;
  const direction = delta < -0.5 ? "dropped" : delta > 0.5 ? "risen" : null;
  if (!direction) return null;
  return `Your 7-day average hunger before meals has ${direction} from ${first.rolling.toFixed(1)} to ${last.rolling.toFixed(1)} since you started logging. Eating to schedule keeps protein in range while appetite is suppressed.`;
}

function NauseaPatternCard() {
  const meals = useGlpMeals(60);

  const stats = useMemo(() => {
    const rows = meals.data ?? [];
    const yes: number[] = [];
    const no: number[] = [];
    const yesKcal: number[] = [];
    const noKcal: number[] = [];
    for (const r of rows) {
      if (r.felt_nauseous == null) continue;
      const fat = Number(r.macros?.f ?? NaN);
      const kcal = Number(r.kcal ?? NaN);
      if (r.felt_nauseous) {
        if (Number.isFinite(fat)) yes.push(fat);
        if (Number.isFinite(kcal)) yesKcal.push(kcal);
      } else {
        if (Number.isFinite(fat)) no.push(fat);
        if (Number.isFinite(kcal)) noKcal.push(kcal);
      }
    }
    return {
      yesCount: yes.length,
      noCount: no.length,
      avgFatYes: avg(yes),
      avgFatNo: avg(no),
      avgKcalYes: avg(yesKcal),
      avgKcalNo: avg(noKcal),
    };
  }, [meals.data]);

  const enough = stats.yesCount >= 5 && stats.noCount >= 5;

  return (
    <section
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <h2 style={{ margin: 0, fontSize: "var(--type-body-l)", fontWeight: 500 }}>
          Nausea patterns
        </h2>
        <span
          className="mono"
          style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}
        >
          {stats.yesCount} yes · {stats.noCount} no · 60d
        </span>
      </header>
      {!enough ? (
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          Need at least 5 nauseous and 5 non-nauseous meals to surface a pattern.
          Keep flagging meals on log.
        </p>
      ) : (
        <NauseaPatternBody stats={stats} />
      )}
    </section>
  );
}

function NauseaPatternBody({
  stats,
}: {
  stats: {
    avgFatYes: number;
    avgFatNo: number;
    avgKcalYes: number;
    avgKcalNo: number;
  };
}) {
  const fatDelta = stats.avgFatYes - stats.avgFatNo;
  const kcalDelta = stats.avgKcalYes - stats.avgKcalNo;
  const driver =
    Math.abs(fatDelta) > Math.abs(kcalDelta) / 5 ? "fat" : "portion size";
  const headline =
    fatDelta > 5
      ? `Higher-fat meals are correlated with nausea (avg ${stats.avgFatYes.toFixed(0)}g fat vs ${stats.avgFatNo.toFixed(0)}g for non-nausea).`
      : kcalDelta > 150
        ? `Bigger meals are correlated with nausea (avg ${stats.avgKcalYes.toFixed(0)} kcal vs ${stats.avgKcalNo.toFixed(0)} for non-nausea).`
        : `No strong ${driver} pattern yet — meals that triggered nausea look similar to those that didn't.`;

  return (
    <>
      <p
        style={{
          margin: 0,
          padding: "var(--space-3)",
          background: "var(--accent-wash)",
          borderRadius: "var(--radius-card)",
          fontSize: "var(--type-body-s)",
          color: "var(--fg)",
          lineHeight: 1.5,
        }}
      >
        {headline}
      </p>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "var(--space-3)",
          margin: 0,
        }}
      >
        <Stat label="Avg fat (nausea)" value={`${stats.avgFatYes.toFixed(0)} g`} />
        <Stat label="Avg fat (no nausea)" value={`${stats.avgFatNo.toFixed(0)} g`} />
        <Stat label="Avg kcal (nausea)" value={`${stats.avgKcalYes.toFixed(0)}`} />
        <Stat label="Avg kcal (no nausea)" value={`${stats.avgKcalNo.toFixed(0)}`} />
      </dl>
    </>
  );
}

function useGlpMeals(days: number) {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["glp1_meals", userId, days],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<MealForGlp[]> => {
      if (!userId) return [];
      const since = new Date();
      since.setDate(since.getDate() - days);
      const { data, error } = await supabase
        .from("meal")
        .select("id, ts, kcal, macros, hunger_before, felt_nauseous")
        .eq("user_id", userId)
        .gte("ts", since.toISOString())
        .order("ts", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MealForGlp[];
    },
  });
}

function avg(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
